/** PROTOTYPE document previews -- THROWAWAY. On-brand HTML stand-ins for the PDFs. */
import React from 'react';
import { CRC } from './moduleInputsData';
import {
  ProjectState, Spread, ModuleKey, MODULE_LABELS,
  machineSummary, money, SpreadPricing, joints,
} from './spreadSplitData';
import { ServicesPriceResult } from './spreadPricingApi';

// Real per-spread engine pricing, keyed by spread id (from spreadRollup.spreadPricingMap).
type PricingById = Record<string, SpreadPricing>;
// Real project welding-services price (billed once), or null when disabled / not yet priced.
type Services = ServicesPriceResult | null;

const CUSTOMER_MODULES: ModuleKey[] = ['welding', 'mt', 'ndt', 'coating', 'platform', 'retention'];
const TODAY = '23-Jun-2026';

export type FileSpec =
  | { key: string; kind: 'project-customer' | 'project-internal'; label: string; audience: 'Customer' | 'Internal' }
  | { key: string; kind: 'spread-customer' | 'spread-internal'; spreadId: string; label: string; audience: 'Customer' | 'Internal' };

export function buildFileList(project: ProjectState): FileSpec[] {
  if (project.reporting === 'project') {
    return [
      { key: 'pc', kind: 'project-customer', label: 'Project · Customer', audience: 'Customer' },
      { key: 'pi', kind: 'project-internal', label: 'Project · Internal', audience: 'Internal' },
    ];
  }
  const list: FileSpec[] = [
    { key: 'pc', kind: 'project-customer', label: 'Project · Customer', audience: 'Customer' },
    { key: 'pi', kind: 'project-internal', label: 'Project · Internal', audience: 'Internal' },
  ];
  project.spreads.forEach((s) => {
    list.push({ key: `sc-${s.id}`, kind: 'spread-customer', spreadId: s.id, label: `${s.name} · Customer`, audience: 'Customer' });
    list.push({ key: `si-${s.id}`, kind: 'spread-internal', spreadId: s.id, label: `${s.name} · Internal`, audience: 'Internal' });
  });
  return list;
}

const Paper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ background: '#fff', maxWidth: 820, margin: '0 auto', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,39,63,0.08)', border: `1px solid ${CRC.border}`, padding: '34px 40px', fontFamily: 'Montserrat, sans-serif' }}>
    {children}
  </div>
);

const Head: React.FC<{ project: ProjectState; title: string; internal?: boolean }> = ({ project, title, internal }) => (
  <div style={{ borderBottom: `3px solid ${CRC.heroRed}`, paddingBottom: 12, marginBottom: 20 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span style={{ fontFamily: 'Funnel Sans', fontWeight: 700, fontSize: 18, color: CRC.heroRed }}>CRC EVANS</span>
      <span style={{ fontSize: 11, color: CRC.heritageGrey }}>{project.refNumber} · {TODAY}</span>
    </div>
    <div style={{ fontFamily: 'Funnel Sans', fontSize: 20, fontWeight: 600, color: CRC.blueprintBlue, marginTop: 6 }}>{title}</div>
    <div style={{ fontSize: 12, color: CRC.textGrey, marginTop: 2 }}>{project.customerName} · {project.projectTitle}</div>
    {internal && <div style={{ marginTop: 6, display: 'inline-block', fontSize: 10, fontWeight: 700, color: CRC.readableRed, background: 'rgba(196,0,31,0.06)', padding: '2px 8px', borderRadius: 999 }}>CRC EVANS — INTERNAL USE ONLY</div>}
  </div>
);

const num: React.CSSProperties = { fontFamily: 'Funnel Sans', fontWeight: 600, textAlign: 'right', color: CRC.blueprintBlue };
const cellp = '7px 8px';

// ---- customer per-spread block ----
const SpreadBlock: React.FC<{ s: Spread; p: SpreadPricing }> = ({ s, p }) => (
  <div style={{ marginTop: 16 }}>
    <div style={{ fontFamily: 'Funnel Sans', fontSize: 13, fontWeight: 600, color: CRC.blueprintBlue }}>
      {s.name} · {joints(s).toLocaleString()} joints
    </div>
    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 4, fontSize: 12 }}>
      <tbody>
        {CUSTOMER_MODULES.map((m) => (
          <tr key={m} style={{ borderBottom: `1px solid ${CRC.rowDivider}` }}>
            <td style={{ padding: cellp, color: CRC.textGrey }}>
              {MODULE_LABELS[m]}{m === 'welding' && <span style={{ color: CRC.heritageGrey }}> — {machineSummary(s)}</span>}
            </td>
            <td style={{ ...num, padding: cellp }}>{money(p[m].price)}</td>
          </tr>
        ))}
        <tr><td style={{ padding: cellp, fontWeight: 700, color: CRC.blueprintBlue }}>{s.name} total</td>
          <td style={{ ...num, padding: cellp, color: CRC.readableRed }}>{money(p.total)}</td></tr>
      </tbody>
    </table>
  </div>
);

// ---- project customer (spread reporting): summary matrix + blocks ----
const ProjectCustomerSpread: React.FC<{ project: ProjectState; pricingById: PricingById; services: Services }> = ({ project, pricingById, services }) => {
  const priced = project.spreads.map((s) => ({ s, p: pricingById[s.id] }));
  const servicesTotal = services?.total ?? 0;
  const grand = priced.reduce((sum, { p }) => sum + p.total, 0) + servicesTotal;
  const totalJoints = project.spreads.reduce((sum, s) => sum + joints(s), 0);
  return (
    <Paper>
      <Head project={project} title="Data 360 Pricing Proposal" />
      <div style={{ fontSize: 12, color: CRC.textGrey, marginBottom: 8 }}>
        Covers {project.spreads.length} spreads, {totalJoints.toLocaleString()} total joints.
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
        <thead>
          <tr style={{ background: CRC.pageBg }}>
            {['Spread', 'Joints', 'Welding', 'MT', 'NDT', 'Coating', 'Plat.+Ret.', 'Total'].map((h, i) => (
              <th key={h} style={{ padding: '7px 8px', fontSize: 9.5, fontWeight: 700, color: CRC.heritageGrey, textTransform: 'uppercase', textAlign: i === 0 ? 'left' : 'right' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {priced.map(({ s, p }) => (
            <tr key={s.id} style={{ borderBottom: `1px solid ${CRC.rowDivider}` }}>
              <td style={{ padding: cellp, color: CRC.blueprintBlue, fontWeight: 600 }}>{s.name}</td>
              <td style={{ ...num, padding: cellp, color: CRC.textGrey }}>{joints(s).toLocaleString()}</td>
              <td style={{ ...num, padding: cellp }}>{money(p.welding.price)}</td>
              <td style={{ ...num, padding: cellp }}>{money(p.mt.price)}</td>
              <td style={{ ...num, padding: cellp }}>{money(p.ndt.price)}</td>
              <td style={{ ...num, padding: cellp }}>{money(p.coating.price)}</td>
              <td style={{ ...num, padding: cellp }}>{money(p.platform.price + p.retention.price)}</td>
              <td style={{ ...num, padding: cellp, color: CRC.readableRed }}>{money(p.total)}</td>
            </tr>
          ))}
          {servicesTotal > 0 && (
            <tr style={{ borderTop: `1px solid ${CRC.rowDivider}` }}>
              <td colSpan={7} style={{ padding: cellp, color: CRC.textGrey }}>Platform services (billed once)</td>
              <td style={{ ...num, padding: cellp }}>{money(servicesTotal)}</td>
            </tr>
          )}
          <tr style={{ borderTop: `2px solid ${CRC.border}` }}>
            <td colSpan={7} style={{ padding: cellp, fontWeight: 700, color: CRC.blueprintBlue }}>PROJECT TOTAL (spreads + project services)</td>
            <td style={{ ...num, padding: cellp, fontSize: 14, color: CRC.readableRed }}>{money(grand)}</td>
          </tr>
        </tbody>
      </table>
      {priced.map(({ s, p }) => <SpreadBlock key={s.id} s={s} p={p} />)}
      <div style={{ marginTop: 22, fontSize: 11, color: CRC.heritageGrey, borderTop: `1px solid ${CRC.border}`, paddingTop: 10 }}>
        BASIS OF QUOTE & ASSUMPTIONS — shown once, applies to all spreads. (Reports per pipe, data complexity, source formats, duration, platform users, terms.)
      </div>
    </Paper>
  );
};

// ---- project customer (project reporting): blended, no spreads shown ----
const ProjectCustomerBlended: React.FC<{ project: ProjectState; pricingById: PricingById; services: Services }> = ({ project, pricingById, services }) => {
  const priced = project.spreads.map((s) => pricingById[s.id]);
  const sum = (m: ModuleKey) => priced.reduce((a, p) => a + p[m].price, 0);
  const servicesTotal = services?.total ?? 0;
  const grand = priced.reduce((a, p) => a + p.total, 0) + servicesTotal;
  return (
    <Paper>
      <Head project={project} title="Data 360 Pricing Proposal" />
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
        <tbody>
          {CUSTOMER_MODULES.map((m) => (
            <tr key={m} style={{ borderBottom: `1px solid ${CRC.rowDivider}` }}>
              <td style={{ padding: '8px', color: CRC.textGrey }}>{MODULE_LABELS[m]}</td>
              <td style={{ ...num, padding: '8px' }}>{money(sum(m))}</td>
            </tr>
          ))}
          {servicesTotal > 0 && (
            <tr style={{ borderBottom: `1px solid ${CRC.rowDivider}` }}>
              <td style={{ padding: '8px', color: CRC.textGrey }}>Platform services (billed once)</td>
              <td style={{ ...num, padding: '8px' }}>{money(servicesTotal)}</td>
            </tr>
          )}
          <tr style={{ borderTop: `2px solid ${CRC.border}` }}>
            <td style={{ padding: '8px', fontWeight: 700, color: CRC.blueprintBlue }}>PROJECT TOTAL</td>
            <td style={{ ...num, padding: '8px', fontSize: 15, color: CRC.readableRed }}>{money(grand)}</td>
          </tr>
        </tbody>
      </table>
      <div style={{ marginTop: 20, fontSize: 11, color: CRC.heritageGrey }}>Project-level reporting: spreads are not shown. Identical in shape to today's single proposal.</div>
    </Paper>
  );
};

// ---- internal: cost / price / margin, optionally per spread ----
const InternalTable: React.FC<{ priced: { name: string; p: SpreadPricing }[]; perSpread: boolean }> = ({ priced, perSpread }) => {
  const rows = perSpread ? priced : [{ name: 'Project (all spreads)', p: mergePricing(priced.map((x) => x.p)) }];
  return (
    <>
      {rows.map(({ name, p }) => (
        <div key={name} style={{ marginTop: 14 }}>
          <div style={{ fontFamily: 'Funnel Sans', fontSize: 13, fontWeight: 600, color: CRC.blueprintBlue }}>{name}</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5, marginTop: 4 }}>
            <thead><tr style={{ background: CRC.pageBg }}>
              {['Module', 'Cost', 'Price', 'Margin'].map((h, i) => <th key={h} style={{ padding: '6px 8px', fontSize: 9.5, fontWeight: 700, color: CRC.heritageGrey, textAlign: i === 0 ? 'left' : 'right', textTransform: 'uppercase' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {CUSTOMER_MODULES.map((m) => (
                <tr key={m} style={{ borderBottom: `1px solid ${CRC.rowDivider}` }}>
                  <td style={{ padding: '6px 8px', color: CRC.textGrey }}>{MODULE_LABELS[m]}</td>
                  <td style={{ ...num, padding: '6px 8px', color: CRC.textGrey }}>{money(p[m].cost)}</td>
                  <td style={{ ...num, padding: '6px 8px' }}>{money(p[m].price)}</td>
                  <td style={{ ...num, padding: '6px 8px', color: CRC.green }}>{p[m].price > 0 ? Math.round((1 - p[m].cost / p[m].price) * 100) : 0}%</td>
                </tr>
              ))}
              <tr><td style={{ padding: '6px 8px', fontWeight: 700, color: CRC.blueprintBlue }}>Total</td>
                <td colSpan={2} style={{ ...num, padding: '6px 8px', color: CRC.readableRed }}>{money(p.total)}</td><td /></tr>
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
};

function mergePricing(ps: SpreadPricing[]): SpreadPricing {
  const acc: any = { total: 0 };
  CUSTOMER_MODULES.forEach((m) => {
    acc[m] = { price: 0, cost: 0, margin: 0 };
    ps.forEach((p) => { acc[m].price += p[m].price; acc[m].cost += p[m].cost; });
  });
  acc.total = ps.reduce((a, p) => a + p.total, 0);
  return acc;
}

const ProjectInternal: React.FC<{ project: ProjectState; pricingById: PricingById; services: Services }> = ({ project, pricingById, services }) => {
  const priced = project.spreads.map((s) => ({ name: s.name, p: pricingById[s.id] }));
  const spreadMode = project.reporting === 'spread';
  const servicesTotal = services?.total ?? 0; // engine books services at cost: price == cost
  const grand = priced.reduce((a, { p }) => a + p.total, 0) + servicesTotal;
  return (
    <Paper>
      <Head project={project} title="Pricing Proposal — Internal" internal />
      <div style={{ fontSize: 12, color: CRC.textGrey }}>Per-module cost / price split at project level{spreadMode ? ', then broken out per spread.' : '.'}</div>
      <InternalTable priced={priced} perSpread={false} />
      {servicesTotal > 0 && (
        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
          <span style={{ color: CRC.textGrey }}>Platform services (billed once, at cost)</span>
          <span style={{ ...num, fontWeight: 600 }}>cost {money(servicesTotal)} · price {money(servicesTotal)}</span>
        </div>
      )}
      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', borderTop: `2px solid ${CRC.border}`, paddingTop: 8 }}>
        <span style={{ fontWeight: 700, color: CRC.blueprintBlue }}>PROJECT GRAND TOTAL</span>
        <span style={{ ...num, color: CRC.readableRed, fontSize: 14 }}>{money(grand)}</span>
      </div>
      {spreadMode && <>
        <div style={{ marginTop: 22, fontFamily: 'Funnel Sans', fontWeight: 600, fontSize: 14, color: CRC.blueprintBlue, borderTop: `1px solid ${CRC.border}`, paddingTop: 12 }}>Spread-level split</div>
        <InternalTable priced={priced} perSpread={true} />
      </>}
    </Paper>
  );
};

const SingleSpreadDoc: React.FC<{ project: ProjectState; s: Spread; p: SpreadPricing; internal: boolean }> = ({ project, s, p, internal }) => {
  return (
    <Paper>
      <Head project={project} title={`${s.name} — ${internal ? 'Internal Split' : 'Pricing Proposal'}`} internal={internal} />
      <div style={{ fontSize: 12, color: CRC.textGrey, marginBottom: 4 }}>{joints(s).toLocaleString()} joints · {machineSummary(s)}</div>
      {internal
        ? <InternalTable priced={[{ name: s.name, p }]} perSpread={true} />
        : <SpreadBlock s={s} p={p} />}
      <div style={{ marginTop: 18, fontSize: 11, color: CRC.heritageGrey }}>Self-contained: this spread carries its own platform + retention, safe to hand over on its own.</div>
    </Paper>
  );
};

export const DocPreview: React.FC<{ project: ProjectState; spec: FileSpec; pricingById: PricingById; services: Services }> = ({ project, spec, pricingById, services }) => {
  if (spec.kind === 'spread-customer' || spec.kind === 'spread-internal') {
    const s = project.spreads.find((x) => x.id === spec.spreadId);
    if (!s) return <Paper>Spread removed.</Paper>;
    return <SingleSpreadDoc project={project} s={s} p={pricingById[s.id]} internal={spec.kind === 'spread-internal'} />;
  }
  if (spec.kind === 'project-internal') return <ProjectInternal project={project} pricingById={pricingById} services={services} />;
  return project.reporting === 'spread'
    ? <ProjectCustomerSpread project={project} pricingById={pricingById} services={services} />
    : <ProjectCustomerBlended project={project} pricingById={pricingById} services={services} />;
};
